import type {
  MpClient,
  MpEvent,
  MpPositionTick,
  MpRoomState,
} from "./types";
import type { AvatarConfig } from "@shared/types";

/**
 * Production transport — talks to the Cloudflare RaceRoom Durable Object via
 * WebSocket. Same protocol as BroadcastTransport: hello / state / tick /
 * set-ready / start-race / finish. Mirror that file for behavior; this one
 * only owns the wire transport.
 *
 * Endpoint: wss://<host>/race/ws/<CODE>
 *
 * Auto-reconnect on transient disconnects (5 attempts with backoff).
 */
export class WebSocketTransport implements MpClient {
  private ws: WebSocket | null = null;
  private url = "";
  private myId = "";
  private listeners: Array<(e: MpEvent) => void> = [];
  private reconnects = 0;
  private hello: {
    asHost: boolean;
    me: { id: string; displayName: string; avatarConfig: AvatarConfig };
    lessonId?: string;
    seed?: number;
  } | null = null;

  async join(opts: {
    code: string;
    asHost: boolean;
    me: { id: string; displayName: string; avatarConfig: AvatarConfig };
    lessonId?: string;
    seed?: number;
  }): Promise<MpRoomState> {
    this.myId = opts.me.id;
    this.hello = { asHost: opts.asHost, me: opts.me, lessonId: opts.lessonId, seed: opts.seed };
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.url = `${proto}://${location.host}/race/ws/${opts.code}`;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Race server didn't respond")), 5000);
      const off = this.on("event", (e) => {
        if (e.type === "state" && e.state.players.some((p) => p.id === this.myId)) {
          clearTimeout(timer);
          off();
          resolve(e.state);
        } else if (e.type === "error") {
          clearTimeout(timer);
          off();
          reject(new Error(e.message));
        }
      });
      this.connect();
    });
  }

  private connect() {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.reconnects = 0;
      if (this.hello) {
        ws.send(
          JSON.stringify({
            type: "hello",
            asHost: this.hello.asHost,
            me: { ...this.hello.me, slotIdx: 0, ready: false, finished: false },
            lessonId: this.hello.lessonId,
            seed: this.hello.seed,
          }),
        );
      }
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") {
          this.emit({ type: "state", state: msg.state });
        } else if (msg.type === "tick") {
          this.emit({ type: "tick", from: msg.from, tick: msg.tick });
        } else if (msg.type === "leave") {
          this.emit({ type: "leave", from: msg.from });
        } else if (msg.type === "error") {
          this.emit({ type: "error", message: msg.message ?? "Battle room error" });
        }
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      this.ws = null;
      // Reconnect with backoff (room may still be alive on the server).
      if (this.reconnects < 5 && this.hello) {
        this.reconnects++;
        setTimeout(() => this.connect(), Math.min(2000, 200 * 2 ** this.reconnects));
      }
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }

  setReady(ready: boolean): void {
    this.send({ type: "set-ready", ready });
  }

  startRace(): void {
    this.send({ type: "start-race" });
  }

  sendTick(tick: MpPositionTick): void {
    this.send({ type: "tick", tick });
  }

  reportFinish(opts: {
    timeMs: number;
    score: number;
    correctCount: number;
    totalQuestions: number;
  }): void {
    this.send({ type: "finish", ...opts });
  }

  on(_event: "event", cb: (e: MpEvent) => void): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  leave(): void {
    this.hello = null;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    this.listeners = [];
  }

  private send(msg: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(msg));
    } catch {
      /* socket gone — onclose handler will reconnect */
    }
  }

  private emit(e: MpEvent) {
    for (const l of this.listeners) l(e);
  }
}
